import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FaPlus, FaEdit, FaTrash, FaUser, FaUsers } from '../../components/Icons';
import { SocioServiceFirestore, Socio } from '../../services/SocioServiceFirestore';
import { theme } from '../../theme';

const Container = styled.div`
  padding: 24px;
  max-width: 1280px;
  margin: 0 auto;
  background-color: ${theme.colors.background.default};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
`;

const Title = styled.h1`
  color: ${theme.colors.primary.main};
  font-weight: ${theme.typography.fontWeights.bold};
  font-size: 2.2rem;
  position: relative;
  padding-bottom: 12px;
  display: flex;
  align-items: center;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 60px;
    height: 4px;
    background-color: ${theme.colors.secondary.main};
    border-radius: 2px;
  }
  
  svg {
    margin-right: 16px;
    color: ${theme.colors.secondary.main};
  }
`;

const Button = styled.button`
  background-color: ${theme.colors.secondary.main};
  color: ${theme.colors.secondary.contrastText};
  border: none;
  border-radius: ${theme.borderRadius.small};
  padding: 12px 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-weight: ${theme.typography.fontWeights.medium};
  transition: all 0.3s ease;
  box-shadow: ${theme.shadows.small};
  
  &:hover {
    background-color: ${theme.colors.secondary.dark};
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.medium};
  }
  
  svg {
    margin-right: 10px;
    font-size: 1.1rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background-color: ${theme.colors.background.paper};
  border-radius: ${theme.borderRadius.medium};
  overflow: hidden;
  box-shadow: ${theme.shadows.medium};
  margin-bottom: 32px;
`;

const Th = styled.th<{ sortable?: boolean }>`
  background-color: ${theme.colors.primary.main};
  color: white;
  text-align: left;
  padding: 16px;
  font-weight: ${theme.typography.fontWeights.semibold};
  position: relative;
  
  &:first-child {
    border-top-left-radius: ${theme.borderRadius.small};
  }
  
  &:last-child {
    border-top-right-radius: ${theme.borderRadius.small};
  }
  
  ${props => props.sortable && `
    cursor: pointer;
    user-select: none;
    transition: background-color 0.2s;
    
    &:hover {
      background-color: ${theme.colors.primary.dark};
    }
    
    &:after {
      content: '⇅';
      margin-left: 8px;
      font-size: 0.8em;
      opacity: 0.7;
    }
  `}
`;

const Td = styled.td`
  padding: 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  transition: background-color 0.2s;
  vertical-align: middle;
  
  tr:last-child & {
    border-bottom: none;
  }
  
  tr:hover & {
    background-color: rgba(0, 0, 0, 0.02);
  }
`;

const ActionButton = styled.button`
  background-color: transparent;
  border: none;
  cursor: pointer;
  margin-right: 12px;
  color: ${theme.colors.primary.main};
  font-size: 1.2rem;
  padding: 6px;
  border-radius: ${theme.borderRadius.small};
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: ${theme.colors.secondary.main};
    background-color: rgba(0, 0, 0, 0.05);
    transform: scale(1.1);
  }
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 48px 24px;
  background-color: ${theme.colors.background.paper};
  border-radius: ${theme.borderRadius.medium};
  box-shadow: ${theme.shadows.medium};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  color: ${theme.colors.text.secondary};
  
  svg {
    font-size: 3.5rem;
    color: ${theme.colors.secondary.main};
    opacity: 0.8;
  }
  
  span {
    font-size: 1.2rem;
    font-weight: ${theme.typography.fontWeights.medium};
  }
`;

// Modal de confirmación
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  background-color: white;
  padding: 25px;
  border-radius: 8px;
  width: 450px;
  box-shadow: 0 5px 25px rgba(0, 0, 0, 0.3);
  animation: slideIn 0.3s ease;
  
  @keyframes slideIn {
    from { transform: translateY(-20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
`;

const ModalTitle = styled.h3`
  margin-top: 0;
  color: ${theme.colors.text};
  font-size: 1.3rem;
  border-bottom: 2px solid ${theme.colors.primary};
  padding-bottom: 10px;
  margin-bottom: 15px;
`;

const ModalButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
`;

const CancelButton = styled(Button)`
  background-color: #6c757d;
  
  &:hover {
    background-color: #5a6268;
  }
`;

const DeleteButton = styled(Button)`
  background-color: #dc3545;
  
  &:hover {
    background-color: #c82333;
  }
`;

// Tipo para la ordenación
type SortField = 'nombre' | 'categoria' | 'equipo' | null;
type SortDirection = 'asc' | 'desc';

const SociosList: React.FC = () => {
  const [socios, setSocios] = useState<Socio[]>([]);
  const [sortedSocios, setSortedSocios] = useState<Socio[]>([]);
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [socioToDelete, setSocioToDelete] = useState<string | null>(null);

  useEffect(() => {
    const fetchSocios = async () => {
      setLoading(true);
      try {
        const sociosFS = await SocioServiceFirestore.getAll();
        setSocios(sociosFS);
        setSortedSocios(sociosFS);
      } catch (err) {
        setError("Error cargando socios");
      } finally {
        setLoading(false);
      }
    };
    fetchSocios();
  }, []);
  
  // Función para ordenar los socios
  const handleSort = (field: SortField) => {
    // Si hacemos clic en el mismo campo, cambiamos la dirección
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  
  // Efecto para aplicar la ordenación
  useEffect(() => {
    if (!sortField) {
      setSortedSocios([...socios]);
      return;
    }
    
    const sorted = [...socios].sort((a, b) => {
      let valueA: string = '';
      let valueB: string = '';
      
      if (sortField === 'nombre') {
        valueA = a.display_name || a.nombre || '';
        valueB = b.display_name || b.nombre || '';
      } else if (sortField === 'categoria') {
        valueA = a.Categoria || '';
        valueB = b.Categoria || '';
      } else if (sortField === 'equipo') {
        valueA = a.EQUIPO || '';
        valueB = b.EQUIPO || '';
      }
      
      // Ordenación insensible a mayúsculas/minúsculas
      valueA = valueA.toLowerCase();
      valueB = valueB.toLowerCase();
      
      if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
      if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    setSortedSocios(sorted);
  }, [socios, sortField, sortDirection]);

  const handleDelete = (socioId: string) => {
    setSocioToDelete(socioId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (socioToDelete) {
      try {
        await SocioServiceFirestore.delete(socioToDelete);
        const updatedSocios = socios.filter(s => s.id !== socioToDelete);
        setSocios(updatedSocios);
      } catch {
        setError("Error eliminando socio");
      }
      setShowDeleteModal(false);
      setSocioToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setSocioToDelete(null);
  };

  const navigate = useNavigate();
  
  return (
    <Container>
      <Header>
        <Title><FaUsers /> Gestión de Socios</Title>
        <Button onClick={() => navigate('/socios/new')}>
          <FaPlus /> Añadir Socio
        </Button>
      </Header>
      
      <ButtonGroup>
        <Button as={Link} to="/socios/import">
          <FaPlus /> Importar Socios
        </Button>
        <Button as={Link} to="/equipos">
          <FaUsers /> Gestión de Equipos
        </Button>
      </ButtonGroup>
      {loading ? (
        <p>Cargando socios...</p>
      ) : error ? (
        <p style={{ color: 'red' }}>{error}</p>
      ) : socios.length > 0 ? (
        <Table>
          <thead>
            <tr>
              <Th style={{ width: '50px' }}></Th>
              <Th 
                sortable 
                onClick={() => handleSort('nombre')}
                style={{
                  backgroundColor: sortField === 'nombre' ? theme.colors.primary.dark : undefined,
                }}
              >
                Nombre {sortField === 'nombre' && (sortDirection === 'asc' ? '↑' : '↓')}
              </Th>
              <Th 
                sortable 
                onClick={() => handleSort('categoria')}
                style={{
                  backgroundColor: sortField === 'categoria' ? theme.colors.primary.dark : undefined,
                }}
              >
                Categoria {sortField === 'categoria' && (sortDirection === 'asc' ? '↑' : '↓')}
              </Th>
              <Th 
                sortable 
                onClick={() => handleSort('equipo')}
                style={{
                  backgroundColor: sortField === 'equipo' ? theme.colors.primary.dark : undefined,
                }}
              >
                Equipo {sortField === 'equipo' && (sortDirection === 'asc' ? '↑' : '↓')}
              </Th>
              <Th>Acciones</Th>
            </tr>
          </thead>
          <tbody>
            {sortedSocios.map(socio => (
              <tr key={socio.id}>
                <Td style={{ width: '50px', textAlign: 'center' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    display: 'inline-block',
                    border: '2px solid #eee',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    <img 
                      src={socio.fotoUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(socio.display_name || socio.nombre || 'Piloto')}&background=eee&color=666&size=64&rounded=true`}
                      alt={socio.display_name || socio.nombre || 'Piloto'}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                      onError={(e) => {
                        e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(socio.display_name || socio.nombre || 'Piloto')}&background=eee&color=666&size=64&rounded=true`;
                      }}
                    />
                  </div>
                </Td>
                <Td>{socio.display_name || socio.nombre || '-'}</Td>
                <Td>{socio.Categoria || '-'}</Td>
                <Td>{socio.EQUIPO || '-'}</Td>
                <Td>
                  <Link to={`/socios/${socio.id}`}>
                    <ActionButton title="Editar piloto">
                      <FaEdit />
                    </ActionButton>
                  </Link>
                  <Link to={`/socios/profile/${socio.id}`}>
                    <ActionButton title="Ver perfil detallado">
                      <FaUser />
                    </ActionButton>
                  </Link>
                  <ActionButton onClick={() => handleDelete(socio.id!)} title="Eliminar piloto">
                    <FaTrash />
                  </ActionButton>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <EmptyMessage>
          <FaUsers />
          <span>No hay socios registrados. Crea un nuevo socio para comenzar.</span>
        </EmptyMessage>
      )}
      
      {/* Modal de confirmación para eliminar */}
      {showDeleteModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalTitle>Confirmar eliminación</ModalTitle>
            <p>¿Estás seguro de que deseas eliminar este socio? Esta acción no se puede deshacer.</p>
            <p>Se eliminarán también todos los resultados asociados a este socio.</p>
            <ModalButtons>
              <CancelButton onClick={cancelDelete}>Cancelar</CancelButton>
              <DeleteButton onClick={confirmDelete}>Eliminar</DeleteButton>
            </ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default SociosList;
